import { useEffect, useRef, useState } from "react";
import { BASE_URL, ENDPOINT_ROUTES } from "../providers/routes";

export const STREAM_STATUS = {
  DISABLED: "disabled",
  CONNECTING: "connecting",
  LIVE: "live",
  OFFLINE: "offline",
};

// Własny backoff zamiast wbudowanego w EventSource: przeglądarka wznawia
// połączenie w kółko co ~3 s i nigdy się nie poddaje. Gdy strumień jest ubijany
// po stronie serwera/proxy, taki reconnect zalewa backend połączeniami.
const RETRY_BASE_MS = 5000;
const RETRY_MAX_MS = 120000;

// Po tylu nieudanych próbach z rzędu uznajemy strumień za martwy (UI: offline).
// Próby lecą dalej, ale już z maksymalnym odstępem.
const FAILURES_BEFORE_OFFLINE = 3;

// Pełne przeładowanie danych jest drogie — nie częściej niż raz na tyle ms.
const RESYNC_MIN_INTERVAL_MS = 30000;

/**
 * Nasłuchuje zmian terminarza przez SSE.
 * Zwraca stan połączenia (STREAM_STATUS) — do wskaźnika w UI.
 *
 * @param {boolean} enabled     np. czy użytkownik jest zalogowany
 * @param {function} onChange   (payload) => void  — pojedyncza zmiana wydarzenia
 * @param {function} onResync   () => void         — pełne przeładowanie danych
 */
export default function useCalendarStream(enabled, onChange, onResync) {
  // trzymamy w refach, żeby zmiana identyczności callbacków nie zrywała połączenia
  const onChangeRef = useRef(onChange);
  const onResyncRef = useRef(onResync);
  const [status, setStatus] = useState(STREAM_STATUS.DISABLED);

  onChangeRef.current = onChange;
  onResyncRef.current = onResync;

  useEffect(() => {
    if (!enabled) {
      setStatus(STREAM_STATUS.DISABLED);
      return;
    }

    let source = undefined;
    let retryTimer = undefined;
    let failures = 0;
    let lastResyncAt = Date.now(); // start = dane właśnie zostały pobrane
    let disposed = false;

    // Strażnik przed lawiną pełnych przeładowań: przy flapującym połączeniu
    // każdy reconnect chciałby pobrać cały zakres dat od nowa.
    const resync = () => {
      const now = Date.now();

      if (now - lastResyncAt < RESYNC_MIN_INTERVAL_MS) return;

      lastResyncAt = now;
      onResyncRef.current?.();
    };

    const dropSource = () => {
      if (source === undefined) return;

      source.onerror = null;
      source.close();
      source = undefined;
    };

    const scheduleReopen = () => {
      clearTimeout(retryTimer);

      const delay = Math.min(
        RETRY_BASE_MS * Math.pow(2, failures - 1),
        RETRY_MAX_MS,
      );

      retryTimer = setTimeout(() => {
        if (disposed || document.visibilityState !== "visible") return;

        open();
      }, delay);
    };

    const open = () => {
      if (disposed || source !== undefined) return;

      setStatus(STREAM_STATUS.CONNECTING);

      source = new EventSource(
        `${BASE_URL}${ENDPOINT_ROUTES.calendar}/stream`,
        { withCredentials: true },
      );

      source.addEventListener("calendar:connected", () => {
        const reconnected = failures > 0;

        failures = 0;
        setStatus(STREAM_STATUS.LIVE);

        // po zerwaniu połączenia mogliśmy przegapić zmiany; przy pierwszym
        // podłączeniu dane są świeżo pobrane, więc nie ma czego dociągać
        if (reconnected) resync();
      });

      source.addEventListener("calendar:changed", (e) => {
        setStatus(STREAM_STATUS.LIVE);

        try {
          onChangeRef.current?.(JSON.parse(e.data));
        } catch (err) {
          console.error("Nieprawidłowa ramka SSE:", err);
        }
      });

      // serwer zgubił kontekst (restart / długa przerwa) — pobierz wszystko od nowa
      source.addEventListener("calendar:resync", () => {
        setStatus(STREAM_STATUS.LIVE);
        resync();
      });

      source.onerror = () => {
        // Przejmujemy reconnect od przeglądarki: zamykamy źródło (inaczej
        // wznawiałaby co ~3 s bez końca) i wracamy z rosnącym odstępem.
        dropSource();

        failures++;
        setStatus(
          failures >= FAILURES_BEFORE_OFFLINE
            ? STREAM_STATUS.OFFLINE
            : STREAM_STATUS.CONNECTING,
        );

        scheduleReopen();
      };
    };

    const close = () => {
      clearTimeout(retryTimer);
      retryTimer = undefined;
      dropSource();
      setStatus(STREAM_STATUS.OFFLINE);
    };

    // Karta w tle nie potrzebuje strumienia. HTTP/1.1 dopuszcza tylko 6 równoległych
    // połączeń na domenę, a każdy otwarty strumień zjada jedno z nich — przy kilku
    // kartach zwykłe żądania AJAX przestałyby przechodzić.
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        open();
        // w tle strumień był zamknięty, więc mogliśmy coś przegapić
        resync();
      } else {
        close();
      }
    };

    if (document.visibilityState === "visible") open();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      close();
    };
  }, [enabled]);

  return status;
}
